import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Table, Pagination, Button } from 'antd';
import type { TableColumnsType } from 'antd';
import EditableDrawer from 'components/EditableDrawer';
import { useQuery, useLazyQuery } from '@apollo/client';
import { TmsUnloadingParcelDetailInfoType } from '../graphql/queries/getTmsUnloadingParcelDetailsByBagIds';
import { GET_UNLOADING_ORDER_DETAIL_WAYPOINT_COUNT_BY_TASK_ID_AND_WAYPOINT_ID, GetUnloadingOrderDetailWaypointCountByTaskIdAndWaypointIdResponse, GetUnloadingOrderDetailWaypointCountByTaskIdAndWaypointIdVariables } from '../graphql/queries/getUnloadingOrderDetailWaypointCountByTaskIdAndWaypointId';
import { GET_UNLOADING_ORDER_DETAILS_BY_WAYPOINT_ID, UnloadingOrderDetailInfoType, GetUnloadingOrderDetailsByWaypointIdResponse, GetUnloadingOrderDetailsByWaypointIdVariables } from '../graphql/queries/getUnloadingOrderDetailsByWaypointId';
import { GET_UNLOADED_ORDER_DETAIL_COUNT, GetUnloadedOrderDetailCountResponse, GetUnloadedOrderDetailCountVariables } from '../graphql/queries/getUnloadedOrderDetailCount';
import { GET_UNLOADED_ORDER_DETAILS, GetUnloadedOrderDetailsResponse, GetUnloadedOrderDetailsVariables } from '../graphql/queries/getUnloadedOrderDetails';
import { TmsTaskWaypointType } from '../graphql/queries/getTmsTaskDetail';
import dayjs from 'dayjs';
import ParcelDetailDrawer from 'components/ParcelDetailDrawer';
import { getUserRole } from 'utils/auth';
import InputFilterItem from 'components/InputFilterItem';
import SingleSelect from 'components/SingleSelect';
import { overallStatusOptions } from 'config/filterOptions';
import { t } from '../../../../i18n';

interface UnloadingDetailDrawerProps {
    open: boolean;
    onClose: () => void;
    waypointId: number; // 这里应该是taskWaypointId，不是waypointId
    taskId?: number; // 任务ID，用于查询包裹和包牌数量
    taskNumber?: string;
    sortingCode?: string;
    unSealTime?: string;
    routeName?: string;
    taskWaypoints?: TmsTaskWaypointType[]; // 任务途经点数据（tms_task_waypoint表），用于构建装车站点选项
    waypointIdToSortingCodeMap?: Map<number, string>; // waypointId到sortingCode的映射，用于显示
    isLinehaulUnload?: boolean; // true=干线卸车（读 tms_loading 已卸子集）；默认 false=支线入库（读 tms_unloading 旧表）
}

const UnloadingDetailDrawer: React.FC<UnloadingDetailDrawerProps> = ({
    open,
    onClose,
    waypointId,
    taskId,
    taskNumber,
    sortingCode: sortingCodeProp,
    unSealTime: unSealTimeProp,
    routeName,
    taskWaypoints = [],
    waypointIdToSortingCodeMap,
    isLinehaulUnload = false
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [parcelDetailVisible, setParcelDetailVisible] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState<string>('');
    const userRole = getUserRole();

    // 筛选条件类型
    interface FilterConditions {
        trackingNumber: string;
        status: string;
        loadingWaypointId: string;
    }

    // 临时筛选条件（用于输入，不会触发查询）
    const [tempFilters, setTempFilters] = useState<FilterConditions>({
        trackingNumber: '',
        status: '',
        loadingWaypointId: ''
    });

    // 实际应用的筛选条件（用于查询）
    const [appliedFilters, setAppliedFilters] = useState<FilterConditions>({
        trackingNumber: '',
        status: '',
        loadingWaypointId: ''
    });

    // 当 drawer 打开时重置分页和筛选，关闭时也重置
    useEffect(() => {
        const emptyFilters: FilterConditions = {
            trackingNumber: '',
            status: '',
            loadingWaypointId: ''
        };
        if (open) {
            // drawer 打开时重置状态
            setCurrentPage(1);
            setPageSize(10);
            setTempFilters(emptyFilters);
            setAppliedFilters(emptyFilters);
        } else {
            // drawer 关闭时也重置状态（双重保险）
            setCurrentPage(1);
            setPageSize(10);
            setTempFilters(emptyFilters);
            setAppliedFilters(emptyFilters);
        }
    }, [open]);

    // 查询当前途径点的包裹和包牌数量
    const { data: inboundCountData, loading: inboundCountLoading } = useQuery<GetUnloadingOrderDetailWaypointCountByTaskIdAndWaypointIdResponse, GetUnloadingOrderDetailWaypointCountByTaskIdAndWaypointIdVariables>(
        GET_UNLOADING_ORDER_DETAIL_WAYPOINT_COUNT_BY_TASK_ID_AND_WAYPOINT_ID,
        {
            variables: {
                taskId: taskId || 0,
                waypointId: waypointId
            },
            skip: !taskId || !waypointId || !open || isLinehaulUnload
        }
    );

    // 干线卸车：计数读 tms_loading_order_detail 已卸子集（waypointId 即 taskWaypointId）
    const { data: unloadedCountData, loading: unloadedCountLoading } = useQuery<GetUnloadedOrderDetailCountResponse, GetUnloadedOrderDetailCountVariables>(
        GET_UNLOADED_ORDER_DETAIL_COUNT,
        {
            variables: { taskId: taskId!, taskWaypointId: waypointId },
            skip: !taskId || !waypointId || !open || !isLinehaulUnload
        }
    );

    // 判断是否有 overallStatus 筛选条件
    const hasStatusFilter = useMemo(() => {
        return appliedFilters.status && appliedFilters.status.trim() !== '';
    }, [appliedFilters.status]);

    // 支线入库：分页明细读 tms_unloading（支持 overallStatus 筛选）
    const [executeInboundQuery, { data: inboundParcelData, loading: inboundLoading }] = useLazyQuery<GetUnloadingOrderDetailsByWaypointIdResponse, GetUnloadingOrderDetailsByWaypointIdVariables>(
        GET_UNLOADING_ORDER_DETAILS_BY_WAYPOINT_ID,
        {
            fetchPolicy: 'no-cache',
            notifyOnNetworkStatusChange: true
        }
    );

    // 干线卸车：分页明细读 tms_loading_order_detail 已卸子集（content 形状与支线入库一致）
    const [executeUnloadedQuery, { data: unloadedParcelData, loading: unloadedLoading }] = useLazyQuery<GetUnloadedOrderDetailsResponse, GetUnloadedOrderDetailsVariables>(
        GET_UNLOADED_ORDER_DETAILS,
        {
            fetchPolicy: 'no-cache',
            notifyOnNetworkStatusChange: true
        }
    );

    // 跟踪当前查询的 waypointId，用于判断数据是否匹配
    const [currentQueryWaypointId, setCurrentQueryWaypointId] = useState<number | null>(null);

    // 当 waypointId 改变时，清空所有状态
    useEffect(() => {
        if (open && waypointId) {
            const emptyFilters: FilterConditions = {
                trackingNumber: '',
                status: '',
                loadingWaypointId: ''
            };
            setCurrentPage(1);
            setPageSize(10);
            setTempFilters(emptyFilters);
            setAppliedFilters(emptyFilters);
            setParcelDetailVisible(false);
            setSelectedOrderId('');
            setTotalParcelCount(0);
            setCurrentQueryWaypointId(null);
        }
    }, [waypointId, open]);

    // 拉取分页明细：按干线/支线分流数据源（支持 overallStatus 筛选）
    useEffect(() => {
        if (!open || !waypointId) {
            setCurrentQueryWaypointId(null);
            return;
        }

        // 获取 loadingWaypointId（干线/支线查询均用）
        let loadingWpId: number | undefined;
        if (appliedFilters.loadingWaypointId && appliedFilters.loadingWaypointId.trim()) {
            const parsed = parseInt(appliedFilters.loadingWaypointId, 10);
            if (!isNaN(parsed) && parsed > 0) {
                loadingWpId = parsed;
            }
        }

        // 根据干线/支线分流到不同数据源；二者 content 形状一致，下游统一处理
        const pageRequest = { page: currentPage, size: pageSize };
        const trackingNumberArg = appliedFilters.trackingNumber?.trim() || undefined;
        const overallStatusArg = hasStatusFilter ? appliedFilters.status?.trim() || undefined : undefined;
        const queryPromise = isLinehaulUnload
            ? executeUnloadedQuery({
                variables: {
                    taskWaypointId: waypointId,
                    pageRequest,
                    trackingNumber: trackingNumberArg,
                    loadingWaypointId: loadingWpId,
                    overallStatus: overallStatusArg
                }
            })
            : executeInboundQuery({
                variables: {
                    waypointId: waypointId,
                    pageRequest,
                    trackingNumber: trackingNumberArg,
                    loadingWaypointId: loadingWpId,
                    overallStatus: overallStatusArg
                }
            });
        queryPromise.then(() => {
            setCurrentQueryWaypointId(waypointId);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, waypointId, hasStatusFilter, appliedFilters, currentPage, pageSize]);

    // loading 状态：按干线/支线取对应来源
    const loading = isLinehaulUnload ? unloadedLoading : inboundLoading;

    // 归一化分页响应（干线/支线 content 形状一致）
    const listResponse = isLinehaulUnload
        ? unloadedParcelData?.getUnloadedOrderDetails
        : inboundParcelData?.getUnloadingOrderDetailsByWaypointId;

    const paginatedData = useMemo(() => {
        if (currentQueryWaypointId !== waypointId) {
            return [];
        }
        return listResponse?.content || [];
    }, [currentQueryWaypointId, waypointId, listResponse]);

    // 获取当前的 totalElements
    const currentTotalElements = useMemo(() => {
        return listResponse?.totalElements || 0;
    }, [listResponse]);

    // 保存总数（不受筛选影响）
    const [totalParcelCount, setTotalParcelCount] = useState<number>(0);

    // 当有新数据且没有筛选条件时，更新总数
    useEffect(() => {
        if (currentQueryWaypointId !== waypointId) {
            return;
        }

        const hasFilters = (appliedFilters.trackingNumber && appliedFilters.trackingNumber.trim()) ||
                           (appliedFilters.status && appliedFilters.status.trim()) ||
                           (appliedFilters.loadingWaypointId && appliedFilters.loadingWaypointId.trim());

        // 只有在没有筛选条件时，才更新总数
        if (!hasFilters && currentTotalElements > 0) {
            setTotalParcelCount(currentTotalElements);
        }
    }, [currentTotalElements, appliedFilters, currentQueryWaypointId, waypointId]);

    // 当 drawer 关闭时重置总数
    useEffect(() => {
        if (!open) {
            setTotalParcelCount(0);
        }
    }, [open]);

    // 应用筛选条件（将临时筛选条件应用到实际筛选条件，触发查询）
    const handleApplyFilter = useCallback(() => {
        setAppliedFilters({
            trackingNumber: tempFilters.trackingNumber.trim(),
            status: tempFilters.status.trim(),
            loadingWaypointId: tempFilters.loadingWaypointId.trim()
        });
        setCurrentPage(1); // 重置到第一页
        // 查询会在 useEffect 中自动触发，因为 appliedFilters 已经更新
    }, [tempFilters]);

    // 重置筛选条件
    const handleResetFilter = useCallback(() => {
        const emptyFilters: FilterConditions = {
            trackingNumber: '',
            status: '',
            loadingWaypointId: ''
        };
        setTempFilters(emptyFilters);
        setAppliedFilters(emptyFilters);
        setCurrentPage(1); // 重置到第一页
        // 查询会在 useEffect 中自动触发，因为 appliedFilters 已经清空
    }, []);

    // 构建站点选项（从 task waypoints 中提取，value是id，label是sortingCode）
    const siteOptions = useMemo(() => {
        return taskWaypoints
            .filter(wp => {
                const sortingCode = waypointIdToSortingCodeMap?.get(wp.waypointId);
                return sortingCode && wp.id;
            })
            .map(wp => {
                const sortingCode = waypointIdToSortingCodeMap?.get(wp.waypointId) || '';
                return {
                    label: sortingCode,
                    value: parseInt(wp.id) // 使用 task waypoint 的 id（tms_task_waypoint 表的 id）
                };
            })
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [taskWaypoints, waypointIdToSortingCodeMap]);

    const handlePageChange = (page: number, size?: number) => {
        setCurrentPage(page);
        if (size) {
            setPageSize(size);
        }
    };

    // 当筛选条件改变时，重置到第一页
    useEffect(() => {
        setCurrentPage(1);
    }, [appliedFilters]);

    const sortingCode = sortingCodeProp || '-';

    const formatTime = (timeStr: string | null | undefined) => {
        if (!timeStr) return '-';
        return dayjs(timeStr).format('YYYY-MM-DD HH:mm:ss');
    };
    
    // 计数按干线/支线取对应来源；干线读 tms_loading 已卸子集，支线读 tms_unloading 旧表
    const countLoading = isLinehaulUnload ? unloadedCountLoading : inboundCountLoading;
    const countData = isLinehaulUnload
        ? unloadedCountData?.getUnloadedOrderDetailCount
        : inboundCountData?.getUnloadingOrderDetailWaypointCountByTaskIdAndWaypointId;
    const parcelCount = countData?.parcelCount ?? totalParcelCount;
    const bagCount = countData?.bagCount ?? 0;

    // 定义统一的数据类型（兼容两种接口返回）
    type ParcelDataItem = TmsUnloadingParcelDetailInfoType | UnloadingOrderDetailInfoType;

    // 处理点击tracking number
    const handleTrackingNumberClick = (parcel: ParcelDataItem) => {
        if (parcel.id) {
            setSelectedOrderId(parcel.id.toString());
            setParcelDetailVisible(true);
        }
    };

    const columns: TableColumnsType<ParcelDataItem> = useMemo(() => [
        {
            title: String(t('tms.task.unloadingDetailDrawer.index')),
            key: 'index',
            align: 'left',
            width: 80,
            render: (_: any, __: any, index: number) => (currentPage - 1) * pageSize + index + 1,
        },
        {
            title: String(t('tms.task.unloadingDetailDrawer.trackingNumber')),
            dataIndex: 'trackingNumber',
            key: 'trackingNumber',
            align: 'left',
            render: (text, record) => (
                <Button
                    type="link"
                    onClick={() => handleTrackingNumberClick(record)}
                    className="text-purple-600 hover:text-purple-800 p-0 h-auto"
                >
                    {text}
                </Button>
            ),
        },
        {
            title: String(t('tms.task.unloadingDetailDrawer.status')),
            dataIndex: 'overallStatus',
            key: 'overallStatus',
            align: 'left',
            render: (status: string | null | undefined) => {
                if (!status) return '-';
                return String(t(`enum.overallStatus.${status}`));
            },
        },
        {
            title: String(t('tms.task.unloadingDetailDrawer.loadingSite')),
            dataIndex: 'loadingWaypointSortingCode',
            key: 'loadingWaypointSortingCode',
            align: 'left',
            render: (value: string | null | undefined) => {
                return value || '-';
            },
        },
        {
            title: String(t('tms.task.unloadingDetailDrawer.bagNumber')),
            dataIndex: 'bagNumber',
            key: 'bagNumber',
            align: 'left',
            render: (value) => value || '-',
        },
        {
            title: String(t('tms.task.unloadingDetailDrawer.bagTaskNumber')),
            dataIndex: 'bagTaskNumber',
            key: 'bagTaskNumber',
            align: 'left',
            width: 180,
            render: (value) => value || '-',
        },
    ], [currentPage, pageSize, routeName, t]);

    return (
        <EditableDrawer
            title={String(t('tms.task.unloadingDetailDrawer.title'))}
            visible={open}
            onClose={onClose}
            width={1000}
            showDefaultFooter={false}
            className="[&_.ant-drawer-body>div]:overflow-visible"
            zIndex={1100}
        >
            <div className="mb-4 leading-6">
                <div className="mb-2">
                    <span className="mr-16">{String(t('tms.task.unloadingDetailDrawer.taskNumber'))} <span className="font-bold">{taskNumber || '-'}</span></span>
                    <span>{String(t('tms.task.unloadingDetailDrawer.site'))} <span className="font-bold">{sortingCode}</span></span>
                </div>
                <div className="mb-2">
                    <span>{String(t('tms.task.unloadingDetailDrawer.unsealingTime'))} <span className="font-bold">{formatTime(unSealTimeProp || null)}</span></span>
                </div>
                <div>
                    <span className="mr-16">{String(t('tms.task.unloadingDetailDrawer.parcelCount'))} <span className="font-bold">{countLoading ? '-' : parcelCount}</span></span>
                    <span>{String(t('tms.task.unloadingDetailDrawer.bagCount'))} <span className="font-bold">{countLoading ? '-' : bagCount}</span></span>
                </div>
            </div>

            {/* 筛选区域 */}
            <div className="mb-4 p-4 bg-gray-50 rounded">
                <div className="flex flex-wrap gap-4">
                    <div className="flex-1 min-w-[200px]">
                        <InputFilterItem
                            label={String(t('tms.task.unloadingDetailDrawer.parcelNumber'))}
                            value={tempFilters.trackingNumber}
                            onChange={(e) => setTempFilters(prev => ({ ...prev, trackingNumber: e.target.value }))}
                            placeholder={String(t('tms.task.unloadingDetailDrawer.enterParcelNumber'))}
                            forceHorizontal={true}
                        />
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <SingleSelect
                            label={String(t('tms.task.unloadingDetailDrawer.parcelStatus'))}
                            options={overallStatusOptions}
                            value={tempFilters.status}
                            onChange={(value) => setTempFilters(prev => ({ ...prev, status: value }))}
                            forceHorizontal={true}
                        />
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <SingleSelect
                            label={String(t('tms.task.unloadingDetailDrawer.loadingSite'))}
                            options={siteOptions}
                            value={tempFilters.loadingWaypointId}
                            onChange={(value) => setTempFilters(prev => ({ ...prev, loadingWaypointId: value }))}
                            forceHorizontal={true}
                        />
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                    <Button
                        type="primary"
                        onClick={handleApplyFilter}
                    >
                        {String(t('components.button.filter'))}
                    </Button>
                    <Button
                        onClick={handleResetFilter}
                    >
                        {String(t('components.button.reset'))}
                    </Button>
                </div>
            </div>

            <Table
                columns={columns}
                dataSource={paginatedData}
                loading={loading}
                rowKey="trackingNumber"
                scroll={{ x: 'max-content'}}
                pagination={false}
                size='middle'
             className="mb-4"/>

            <div className="mt-4 flex justify-end">
                <Pagination
                    current={currentPage}
                    pageSize={pageSize}
                    total={currentQueryWaypointId === waypointId ? currentTotalElements : 0}
                    showSizeChanger
                    showQuickJumper
                    showTotal={(total) => t('table.pagination.totalItems', { total })}
                    pageSizeOptions={['10', '20', '50', '100']}
                    onChange={handlePageChange}
                    onShowSizeChange={handlePageChange}
                />
            </div>

            {/* 订单详情抽屉 */}
            {selectedOrderId && (
                <ParcelDetailDrawer
                    visible={parcelDetailVisible}
                    onClose={() => setParcelDetailVisible(false)}
                    orderId={selectedOrderId}
                    userRole={userRole}
                    zIndex={1200}
                />
            )}
        </EditableDrawer>
    );
};

export default UnloadingDetailDrawer;

